import {
  MEDICAL_INSURANCE_DISCOUNT_CATEGORY_ID,
  SELF_INSURANCE_PROVIDER_KEY,
} from 'modules/orders/constants';

function findAlternativesGroupForRow(row, productsAlternatives) {
  const list = Array.isArray(productsAlternatives) ? productsAlternatives : [];
  return list.find(pa => {
    const keyMatch =
      pa.itemProductKey === row.orderItemKey ||
      pa.itemProductKey === row.serviceKey ||
      (row.orderItemKey != null && String(pa.itemProductKey) === String(row.orderItemKey));
    const shapeMatch = Number(pa.itemShapeId) === Number(row.productShapeId);
    return keyMatch && shapeMatch;
  });
}

function lineItemFromInsuranceRow(row, productsAlternatives) {
  const { prescriptionKey } = row;
  const altGroup = findAlternativesGroupForRow(row, productsAlternatives);
  const alt = altGroup?.alternatives?.[0];
  if (alt) {
    return {
      productShapeId: Number(alt.itemShapeId) || 0,
      productKey: alt.itemProductKey,
      quantity: Number(alt.itemQuantity) || 0,
      price: Number(alt.itemPrice) || 0,
      prescriptionKey,
    };
  }
  return {
    productShapeId: Number(row.productShapeId) || 0,
    productKey: row.serviceKey,
    quantity: Number(row.quantity) || 0,
    price: Number(row.price ?? row.servicePrice) || 0,
    prescriptionKey,
  };
}

function groupInsuranceRowsByPrescriptionKey(insuranceRows) {
  const map = new Map();
  insuranceRows.forEach(row => {
    const raw = row.prescriptionKey;
    const key =
      raw != null && String(raw).trim() !== '' ? String(raw).trim() : '__null_prescription__';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  });
  return map;
}

function resolveMedicalCardKey(patientInsurance) {
  const raw = patientInsurance?.patientInsuranceKey ?? null;
  return raw != null && String(raw).trim() !== '' ? String(raw).trim() : null;
}

function resolveHealthPlanKey(patientInsurance) {
  const fromPlan = patientInsurance?.healthPlanKey ?? null;
  if (fromPlan != null && String(fromPlan).trim() !== '') return String(fromPlan).trim();
  return null;
}

/** Discount API `providerKey` — backend order field `AssignedLocation`. */
function resolveDiscountProviderKeyFromOrder(details) {
  const raw = details?.AssignedLocation ?? details?.assignedLocation ?? null;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s !== '' ? s : null;
}

export function isMedicalInsuranceOrder(orderDetails) {
  const details = orderDetails?.details;
  const key = details?.patientInsurance?.insuranceProviderKey;
  return Boolean(key && key !== SELF_INSURANCE_PROVIDER_KEY);
}

/** Any order with patient insurance (medical or self-insurance) — discount API applies to e-Rx lines for both. */
export function isOrderEligibleForInsuranceDiscountCalculate(orderDetails) {
  const details = orderDetails?.details;
  if (!details?.key) return false;
  return details?.patientInsurance?.insuranceProviderKey === SELF_INSURANCE_PROVIDER_KEY;
}

export function getMedicalInsuranceInvoiceRows(dispensedOrderItems) {
  const list = Array.isArray(dispensedOrderItems) ? dispensedOrderItems : [];
  return list.filter(item => item.prescriptionKey != null);
}

/**
 * Builds POST /Discounts/calculate body. Returns null if required keys are missing
 * (health plan, invoice rows with prescription keys, etc.).
 */
export function buildMedicalInsuranceDiscountCalculatePayload({
  orderDetails,
  dispensedOrderItems,
  productsAlternatives,
}) {
  const details = orderDetails?.details;
  if (!details?.key || !isOrderEligibleForInsuranceDiscountCalculate(orderDetails)) return null;

  const patientInsurance = details.patientInsurance || {};
  const medicalCardKey = resolveMedicalCardKey(patientInsurance);
  const healthPlanKey = resolveHealthPlanKey(patientInsurance);
  if (!healthPlanKey) return null;

  const insuranceRows = getMedicalInsuranceInvoiceRows(dispensedOrderItems);
  if (!insuranceRows.length) return null;

  const providerKey = resolveDiscountProviderKeyFromOrder(details);
  if (!providerKey) return null;

  const voucherCode =
    details.promoCode != null && String(details.promoCode).trim() !== '' ? details.promoCode : null;

  const grouped = groupInsuranceRowsByPrescriptionKey(insuranceRows);
  const providers = [];
  grouped.forEach((rows, rxKey) => {
    const prescriptionKey = rxKey === '__null_prescription__' ? null : rxKey;
    const lineItems = rows.map(row => lineItemFromInsuranceRow(row, productsAlternatives));
    const entry = {
      providerKey,
      context: {
        prescriptionKey,
        lineItems,
      },
    };
    providers.push(entry);
  });

  return {
    categoryId: MEDICAL_INSURANCE_DISCOUNT_CATEGORY_ID,
    medicalCardKey,
    healthPlanKey,
    voucherCode,
    utilizationKey: details.key,
    providers,
  };
}

/** Stable key for matching discount `lineItem` rows to e-prescription medicine rows. */
export function rxDiscountLineItemLookupKey(productKey, productShapeId, quantity) {
  const pk = productKey != null ? String(productKey).trim() : '';
  const ps = productShapeId != null ? String(productShapeId).trim() : '';
  const q = quantity != null ? String(quantity).trim() : '';
  return `${pk}|${ps}|${q}`;
}

/** Resolves `{ priceBefore, priceAfter }` from `mapDiscountCalculateResponseToLinePriceLookup` for one catalog line. */
export function getInsuranceLineDiscountFromLookup(lookup, productKey, productShapeId, quantity) {
  if (!lookup) return null;
  const key = rxDiscountLineItemLookupKey(productKey, productShapeId, quantity);
  const row = key ? lookup[key] : null;
  if (
    !row ||
    row.priceBefore == null ||
    row.priceAfter == null ||
    !Number.isFinite(Number(row.priceBefore)) ||
    !Number.isFinite(Number(row.priceAfter))
  ) {
    return null;
  }
  return row;
}

/**
 * Parses POST /Discounts/calculate response body (`{ statusCode, data }` as returned by axios `.data`).
 * Returns a plain object map lookupKey → { priceBefore, priceAfter }.
 */
export function mapDiscountCalculateResponseToLinePriceLookup(apiEnvelope) {
  const inner = apiEnvelope?.data ?? apiEnvelope;
  const providers = inner?.providersDiscountResult;
  const lookup = {};
  if (!Array.isArray(providers)) return lookup;

  const toFiniteNumberOrNull = value => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  providers.forEach(p => {
    const lineRows = p?.lineItemDiscounts;

    if (!Array.isArray(lineRows)) return;

    lineRows.forEach(row => {
      const li = row?.lineItem;
      if (!li) return;
      const { productKey, productShapeId, quantity, price } = li;

      if (productKey == null || productShapeId == null || quantity == null) return;

      const key = rxDiscountLineItemLookupKey(productKey, productShapeId, quantity);
      if (!key) return;

      const priceBefore = toFiniteNumberOrNull(price);
      const qty = Number(quantity);
      const rawPriceAfter = toFiniteNumberOrNull(row?.priceAfterDiscount);
      const priceAfter =
        rawPriceAfter != null && Number.isFinite(qty) && qty > 0 ? rawPriceAfter / qty : null;

      // Preserve first valid row per key; avoid silently writing invalid zeros.
      if (
        !Object.prototype.hasOwnProperty.call(lookup, key) &&
        priceBefore != null &&
        priceAfter != null
      ) {
        lookup[key] = {
          priceBefore,
          priceAfter,
        };
      }
    });
  });
  return lookup;
}

export function rxDiscountLineKeyFromPrescriptionMedicineItem(item) {
  const productKey = item?.itemKey ?? null;
  const productShapeId = item?.productShapeId ?? null;
  const quantity = item?.quantity ?? null;
  if (productKey == null || productShapeId == null || quantity == null) return null;
  return rxDiscountLineItemLookupKey(productKey, productShapeId, quantity);
}
